import { useState, type ReactNode } from 'react';
import { IconBriefcase, IconCoin, IconFileText, IconMail, IconNote, IconReceipt, IconTruck, IconUsers } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import { useToast } from '@hana/ui';
import './i18n';
import { MY_TASKS } from './data';
import { AR_SALE, ESC_SALE, LICH_SALE, OT_SALE, QUOTE_SALE, SRC_SALE, type BlkRow } from './side';
import s from './home.module.css';
import c from './side.module.css';

// Mint-Home (1536) · N-04i: cột phải vai sale — theo code board: lich* (trục 8:00–17:30, 3 làn), sla.*, src.*, blkAR / blkQuote (barRows).
const AX0 = 8, SPAN = 9.5, NOWH = 9 + 14 / 60;
const hm = (h: number) => `${String(Math.floor(h)).padStart(2, '0')}:${String(Math.round((h % 1) * 60)).padStart(2, '0')}`;

export function Lich({ empty }: { empty: boolean }) {
  const t = useT();
  const toast = useToast();
  const [mode, setMode] = useState<'day' | '48'>('day');
  const days = mode === '48' ? 2 : 1;
  const tot = SPAN * days;
  const pos = (d: number, h: number) => ((d * SPAN + Math.min(Math.max(h, AX0), AX0 + SPAN) - AX0) / tot) * 100;
  const li = empty ? [] : LICH_SALE;
  const inWin = li.filter((x) => x[2] >= 0 && x[2] < days && x[3] >= AX0 && x[3] <= AX0 + SPAN);
  const gone = li.filter((x) => x[2] < 0).length;
  const later = li.filter((x) => x[2] >= days || (x[2] >= 0 && x[2] < days && x[3] > AX0 + SPAN)).length;
  const over = li.filter((x) => x[2] < 0 || (x[2] === 0 && x[3] < NOWH)).length;
  const ew = days === 1 ? 20 : 11;
  const lanes: number[] = [];
  const evs = inWin.slice().sort((a, b) => a[2] * 24 + a[3] - (b[2] * 24 + b[3])).map(([kind, id, d, h, href]) => {
    const left = Math.min(pos(d, h), 100 - ew);
    let l = 0;
    while (lanes[l] != null && lanes[l]! > left) l++;
    lanes[l] = left + ew + 0.6;
    const isOver = d === 0 && h < NOWH;
    const soon = !isOver && d === 0 && h - NOWH < 3;
    return { kind, id, href, left, top: 26 + l * 42, time: `${d ? 'mai ' : ''}${hm(h)}`, cls: isOver ? c.eErr : soon ? c.eWarn : c.ePri, title: `${kind} · ${id} · ${d ? '23' : '22'}/Oct/2026 ${hm(h)}` };
  });
  const hours: { k: string; left: number; end?: boolean }[] = [];
  for (let d = 0; d < days; d++) for (let h = AX0; h < AX0 + SPAN; h += days === 1 ? 1 : 2) hours.push({ k: hm(h), left: pos(d, h) });
  hours.push({ k: hm(AX0 + SPAN), left: 100, end: true });
  const H = Math.max(26 + lanes.length * 42, 60);
  const nowL = (NOWH - AX0) / tot * 100;
  return (
    <section className={s.my} aria-label={t('hn.sd.calAria')}>
      <div className={c.h}>
        <h2>{t('hn.sd.cal')}</h2>
        {over > 0 && <span className={c.over}>{t('hn.sd.overN', { n: over })}</span>}
        <span className={c.hsub}>{t('hn.sd.calSub')}</span>
        <div className={s.seg} role="group" aria-label={t('hn.sd.rangeL')}>
          <button type="button" aria-pressed={mode === 'day'} onClick={() => setMode('day')}>{t('hn.sd.today')}</button>
          <button type="button" aria-pressed={mode === '48'} onClick={() => setMode('48')}>{t('hn.sd.h48')}</button>
        </div>
      </div>
      <div className={c.gl}><span className={c.gone}>{gone ? t('hn.sd.gone', { n: gone }) : ''}</span><span>{later ? t('hn.sd.later', { n: later }) : ''}</span></div>
      <div className={c.tl} style={{ height: H }}>
        <div className={c.axis}>{hours.map((h) => <span key={h.k + h.left} style={{ left: `${h.left}%`, transform: h.end ? 'translateX(-100%)' : undefined }}>{h.k}</span>)}</div>
        {mode === '48' && <div className={c.dv} style={{ left: `${(SPAN / tot) * 100}%` }}><span>{t('hn.sd.tmrDay')}</span></div>}
        {evs.map((e) => (
          <a key={e.id + e.kind} href={e.href} title={e.title} className={`${c.ev} ${e.cls}`} style={{ left: `${e.left}%`, top: e.top, width: `${ew}%` }} onClick={(x) => { x.preventDefault(); toast({ msg: t('home.later') }); }}>
            <span>{e.kind} · {e.time}</span><span>{e.id}</span>
          </a>
        ))}
        {!empty && <div className={c.now} aria-hidden="true" style={{ left: `${nowL}%` }}><span>09:14</span></div>}
      </div>
      {!inWin.length && <div className={s.none}>{t('hn.sd.noMark')}</div>}
    </section>
  );
}

export function Sla({ empty }: { empty: boolean }) {
  const t = useT();
  const ot = OT_SALE;
  const now = (ot[ot.length - 1] ?? 0) / 100;
  const sx = (i: number) => 16 + i * (288 / (ot.length - 1));
  const sy = (v: number) => 92 - ((v - 70) / 30) * 76;
  const line = ot.map((v, i) => `${i ? 'L' : 'M'}${sx(i).toFixed(1)} ${sy(v).toFixed(1)}`).join(' ');
  const cnt = (g: string) => (empty ? 0 : MY_TASKS.filter((x) => x.grp === g).length);
  const segs = [['Quá hạn', cnt('over'), 'var(--err)'], ['Hôm nay', cnt('today'), 'var(--d-blue)'], ['Chờ bên ngoài', 0, 'var(--lb)'], ['Sắp tới', cnt('next'), 'var(--d-gray)']] as const;
  const esc = empty ? [] : ESC_SALE;
  return (
    <section className={s.my} aria-label={t('hn.sd.slaAria')}>
      <div className={c.h2}><h2>{t('hn.sd.sla')}</h2><span>{t('hn.sd.slaSub')}</span></div>
      <div className={c.sla}>
        <svg role="img" aria-label={t('hn.sd.gaugeL', { p: Math.round(now * 100) })} width="150" height="82" viewBox="0 0 190 104" className={c.gauge}>
          <path d="M 15 95 A 80 80 0 0 1 175 95" fill="none" stroke="var(--line)" strokeWidth="14" strokeLinecap="round" />
          <path d="M 15 95 A 80 80 0 0 1 175 95" fill="none" stroke="var(--d-blue)" strokeWidth="14" strokeLinecap="round" strokeDasharray="251.3 251.3" strokeDashoffset={(251.3 * (1 - now)).toFixed(1)} />
          <text x="95" y="82" textAnchor="middle" fontSize="28" fontWeight="550" fill="var(--tx)">{Math.round(now * 100)}%</text>
        </svg>
        <svg role="img" aria-label={t('hn.sd.trendL', { v: ot.map((v, i) => `T${38 + i} ${v}%`).join(', ') })} width="100%" height="112" viewBox="0 0 320 112" className={c.trend}>
          <line x1="16" y1="92" x2="304" y2="92" stroke="var(--line)" />
          <path d={`${line} L${sx(ot.length - 1).toFixed(1)} 92 L${sx(0).toFixed(1)} 92 Z`} fill="var(--d-blue)" opacity="0.08" />
          <path d={line} fill="none" stroke="var(--d-blue)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          {ot.map((v, i) => (
            <g key={i}>
              <circle cx={sx(i)} cy={sy(v)} r={i === ot.length - 1 ? 4 : 2.5} fill="var(--card)" stroke="var(--d-blue)" strokeWidth="2" />
              <text x={sx(i)} y={sy(v) - 8} textAnchor="middle" fontSize="10" fontWeight="550" fill="var(--tx)">{v}%</text>
              <text x={sx(i)} y="106" textAnchor="middle" fontSize="10" fill="var(--lb)">T{38 + i}</text>
            </g>
          ))}
        </svg>
      </div>
      <div className={c.seg0}>{t('hn.sd.byGrp')}</div>
      <div role="img" aria-label={`Việc mở: ${segs.map(([k, n]) => `${k} ${n}`).join(', ')}`} className={c.segs}>{segs.map(([k, n, col]) => <div key={k} style={{ flex: `${n} 1 0`, background: col }} />)}</div>
      <div className={c.lg}>{segs.map(([k, n, col]) => <span key={k}><i style={{ background: col }} />{k} <b>{n}</b></span>)}</div>
      {esc.length > 0 && <div className={c.esc0}>{t('hn.sd.esc')}</div>}
      {esc.map(([code, k, path, late], i) => (
        <div key={code + i} className={c.esc} style={{ borderTopWidth: i ? 1 : 0 }}>
          <span className={`${s.code} ${s.e}`}>{code}</span>
          <div className={s.rb}><div className={s.rt}>{k}</div><div className={c.path}>{path}</div></div>
          <span className={c.late}>{late}</span>
        </div>
      ))}
    </section>
  );
}

const SRC_ICON: Record<string, ReactNode> = {
  job: <IconBriefcase size={16} stroke={1.8} aria-hidden="true" />, mail: <IconMail size={16} stroke={1.8} aria-hidden="true" />, rate: <IconFileText size={16} stroke={1.8} aria-hidden="true" />,
  sale: <IconUsers size={16} stroke={1.8} aria-hidden="true" />, fin: <IconCoin size={16} stroke={1.8} aria-hidden="true" />, svc: <IconReceipt size={16} stroke={1.8} aria-hidden="true" />,
  tms: <IconTruck size={16} stroke={1.8} aria-hidden="true" />, memo: <IconNote size={16} stroke={1.8} aria-hidden="true" />,
};

export function Src({ empty }: { empty: boolean }) {
  const t = useT();
  const toast = useToast();
  const items = empty ? [] : SRC_SALE;
  const mx = Math.max(1, ...items.map((x) => x[2]));
  return (
    <section className={s.my} aria-label={t('hn.sd.srcAria')}>
      <div className={c.h2}><h2>{t('hn.sd.src')}</h2><span>{t('hn.sd.srcSub')}</span></div>
      {!items.length && <div className={s.none}>{t('hn.sd.noOpen')}</div>}
      <div role="img" aria-label={t('hn.sd.srcL', { v: items.map(([, k, n]) => `${k} ${n}`).join(', ') })} className={c.src}>
        {items.map(([key, k, n]) => (
          <button key={key} type="button" className={c.sb} onClick={() => toast({ msg: t('home.later') })}>
            <span className={c.sk}>{SRC_ICON[key]}{k}</span>
            <span className={c.track}><span style={{ width: `${Math.max(4, Math.round((n / mx) * 100))}%`, background: key === 'mail' ? 'var(--d-violet)' : 'var(--d-blue)' }} /></span>
            <b>{n}</b>
          </button>
        ))}
      </div>
    </section>
  );
}

const RC = { err: c.rErr, warn: c.rWarn, tx: undefined } as const;
const BAR = { err: 'var(--err)', warn: 'var(--warn)', tx: 'var(--d-blue)' } as const;

export function Blk({ title, count, rows, bars, empty }: { title: string; count: string; rows: readonly BlkRow[]; bars?: boolean; empty: boolean }) {
  const t = useT();
  const toast = useToast();
  const list = empty ? [] : rows;
  const nums = rows.map((r) => parseInt(r.r, 10) || 0);
  const mx = Math.max(1, ...nums);
  return (
    <section className={`${s.my} ${c.blk}`} aria-label={title}>
      <div className={c.h2}><h2>{title}</h2><a href="/ezy/ar" onClick={(e) => { e.preventDefault(); toast({ msg: t('home.later') }); }}>{empty ? '0' : count} ›</a></div>
      {!list.length && <div className={s.none}>{t('hn.sd.noItem')}</div>}
      {list.map((r, i) => (
        <div key={r.k} className={c.br}>
          <div className={s.rb}>
            <div className={c.bt}><span className={s.rt}>{r.k}</span>{r.chip && <span className={`${c.chip} ${r.chip[1] === 'err' ? c.cErr : c.cN}`}>{r.chip[0]}</span>}</div>
            <div className={c.path}>{r.sub}</div>
            {bars && <div role="img" aria-label={`${r.k}: ${r.r}`} className={c.bar}><div style={{ width: `${Math.max(3, Math.round(((nums[i] ?? 0) / mx) * 100))}%`, background: BAR[r.rc] }} /></div>}
          </div>
          <span className={`${c.rv} ${RC[r.rc] ?? ''}`}>{r.r}</span>
          <button type="button" className={`${s.bt} ${s.pri}`} onClick={() => toast({ msg: `${r.act} · ${r.k}` })}>{r.act}</button>
        </div>
      ))}
    </section>
  );
}

export function Side({ empty }: { empty: boolean }) {
  const t = useT();
  return (
    <>
      <Lich empty={empty} /><Sla empty={empty} /><Src empty={empty} />
      <Blk title={t('hn.sd.arT')} count={t('hn.sd.arN', { n: 6 })} rows={AR_SALE} bars empty={empty} />
      <Blk title={t('hn.sd.qtT')} count="5" rows={QUOTE_SALE} empty={empty} />
    </>
  );
}
